import { useEffect, useRef } from 'react';
import { RoomEvent, type Room } from 'livekit-client';
import { fromApiCamLayout, type CamLayoutConfig } from '@/lib/camLayouts';

/** Préférence caméras telle que le back la publie dans la metadata de la room LiveKit. */
export function readCamLayoutFromRoomMetadata(metadata?: string): CamLayoutConfig | null {
  if (!metadata) return null;
  try {
    const data = JSON.parse(metadata) as Record<string, unknown>;
    return fromApiCamLayout(data?.camLayout);
  } catch {
    return null;
  }
}

/**
 * Applique la préférence caméras portée par la metadata de room : le back est la source de vérité
 * (défaut du workspace à la création de la room, puis écritures des régies), donc régies et
 * compositor lisent le même flux d'évènements.
 */
export function useRoomCamLayout(room: Room, onCamLayout: (config: CamLayoutConfig) => void) {
  const onCamLayoutRef = useRef(onCamLayout);
  useEffect(() => {
    onCamLayoutRef.current = onCamLayout;
  }, [onCamLayout]);

  useEffect(() => {
    const sync = (metadata?: string) => {
      const config = readCamLayoutFromRoomMetadata(metadata);
      if (config) onCamLayoutRef.current(config);
    };
    const refreshFromRoom = () => sync(room.metadata);
    refreshFromRoom();
    room.on(RoomEvent.RoomMetadataChanged, sync);
    room.on(RoomEvent.Connected, refreshFromRoom);
    room.on(RoomEvent.Reconnected, refreshFromRoom);
    return () => {
      room.off(RoomEvent.RoomMetadataChanged, sync);
      room.off(RoomEvent.Connected, refreshFromRoom);
      room.off(RoomEvent.Reconnected, refreshFromRoom);
    };
  }, [room]);
}
